import React from "react";
import type { UpsellProductsBlock } from "@/types/product";
import { useProduct } from "@/hooks/useProduct";
import { useIsB2b } from "@/hooks/useIsB2b";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { isProductDiscounted } from "@/utils/discount";
import { useLocalizedTexts, t } from "@/utils/translations";
import { fixedCmsImage } from "@/utils/cmsImage";
import shared from "../ProductDetailContent.module.css";
import styles from "./UpsellProductsSection.module.css";

interface UpsellProductsSectionProps {
  block: UpsellProductsBlock;
}

export const UpsellProductsSection: React.FC<UpsellProductsSectionProps> = ({
  block,
}) => {
  const localizedTexts = useLocalizedTexts();
  const { productData } = useProduct();
  const isB2b = useIsB2b();
  const items = productData.upsellProducts ?? [];
  if (!items.length) return null;

  const heading =
    block.heading?.trim() ||
    t(
      localizedTexts,
      "Tracker.Products.UpsellProductsHeading",
      "Looking at the rest of the range?"
    );
  const subheading =
    block.subheading?.trim() ||
    t(localizedTexts, "Tracker.Products.UpsellProductsSubheading", "");
  const fromLabel = t(localizedTexts, "Tracker.Products.PriceFrom", "From");

  return (
    <section className={`${shared.section} ${shared.bgVeryDark}`}>
      <div className={shared.inner}>
        <h2 className={styles.heading}>{heading}</h2>
        {subheading && <p className={styles.subheading}>{subheading}</p>}
        <div className={styles.grid}>
          {items.map((item) => {
            const body = (
              <>
                <div className={styles.pshot}>
                  {item.image?.url && (
                    <img
                      // At most 80% of the 120px product shot.
                      {...fixedCmsImage(item.image.url, { width: 96 })}
                      alt={item.header || ""}
                      loading="lazy"
                    />
                  )}
                </div>
                <div className={styles.body}>
                  {item.header && (
                    <h3 className={styles.title}>{item.header}</h3>
                  )}
                  {item.description && (
                    <p className={styles.text}>{item.description}</p>
                  )}
                  {isB2b &&
                  (item.price?.formattedListPrice ||
                    item.price?.formattedCalculatedPriceWithoutVat) ? (
                    <DiscountedUnitPrice
                      variant="b2b"
                      standardInclVat={item.price.formattedListPrice}
                      accentPrice={
                        item.price.formattedCalculatedPriceWithoutVat
                      }
                      size="sm"
                      onDark
                    />
                  ) : !isB2b &&
                    isProductDiscounted(item.price) &&
                    item.price?.formattedListPrice &&
                    item.price?.formattedCalculatedPrice ? (
                    <DiscountedUnitPrice
                      variant="discount"
                      standardInclVat={item.price.formattedListPrice}
                      accentPrice={item.price.formattedCalculatedPrice}
                      size="sm"
                      inline
                      onDark
                    />
                  ) : (
                    item.price?.formattedCalculatedPrice && (
                      <span className={styles.from}>
                        {fromLabel} {item.price.formattedCalculatedPrice}
                      </span>
                    )
                  )}
                </div>
              </>
            );
            const rawHref = item.link?.url;
            const isSafeHref =
              !!rawHref && !/^\s*(javascript|data|vbscript):/i.test(rawHref);
            const opensInNewTab = item.link?.target === "_blank";
            return isSafeHref ? (
              <a
                key={item.id}
                href={rawHref}
                target={opensInNewTab ? "_blank" : undefined}
                rel={opensInNewTab ? "noopener noreferrer" : undefined}
                className={styles.card}
              >
                {body}
              </a>
            ) : (
              <div key={item.id} className={styles.card}>
                {body}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UpsellProductsSection;
